/**
 * @file as-file-handle.ts
 * AsFileHandle — the single file value type used across the file APIs
 * (`as-file-selected` from <as-file-upload>, and `as-dropped-files` /
 * `as-app-dropped-files` from drop zones).
 *
 * Lives in its own module so both as-file-upload.ts and fileDrop.ts can import
 * it without a circular dependency. Re-exported from as-file-upload.ts for
 * backward compatibility with existing import paths.
 */

/**
 * A selected file. Mirrors the native `File` surface (`name`, `type`, `size`,
 * `arrayBuffer()`, `text()`) so code written against `<input type=file>` works
 * unchanged, and adds `blob` / `toFile()` / `dataUrl()` for the cases File
 * doesn't cover. Immutable, no caching — each conversion reads the source.
 */
export class AsFileHandle {
  /** Like File.name — filename incl. extension, e.g. "part.stl". */
  readonly name: string;
  /** Like File.type — MIME, or '' when unknown (exactly as File reports it).
   *  For an extension, parse `name` (e.g. `name.split('.').pop()`). */
  readonly type: string;

  private readonly _source: File | Blob;

  constructor(source: File | Blob, name?: string, type?: string) {
    this._source = source;
    const isFile = source instanceof File;
    this.name = name ?? (isFile ? (source as File).name : '');
    this.type = type || source.type; // '' when unknown, same as File
  }

  /** Like File.size — bytes. */
  get size(): number { return this._source.size; }

  /** Lowercased file extension without the dot (e.g. "stl"), derived from `name`.
   *  `''` when the name has no extension. Use this instead of parsing `type`,
   *  which is the MIME type and is often empty for meshes. */
  get extension(): string {
    const dot = this.name.lastIndexOf('.');
    return dot > 0 ? this.name.slice(dot + 1).toLowerCase() : '';
  }

  /** The filename without its extension (e.g. "part" for "part.stl"), preserving
   *  case. The whole `name` when there is no extension. Complements `extension`. */
  get stem(): string {
    const dot = this.name.lastIndexOf('.');
    return dot > 0 ? this.name.slice(0, dot) : this.name;
  }

  /** Like File.arrayBuffer(). */
  arrayBuffer(): Promise<ArrayBuffer> { return this._source.arrayBuffer(); }

  /** Like File.text(). */
  text(): Promise<string> { return this._source.text(); }

  /** The raw Blob — for Blob-taking APIs (fetch body, FormData, configurator upload). */
  get blob(): Blob { return this._source; }

  /** A real File, always. Returns the original File, or synthesizes one from the
   *  Blob (host-provider / GrabCAD imports). Never null. */
  toFile(): File {
    return this._source instanceof File
      ? this._source
      : new File([this._source], this.name, { type: this.type || 'application/octet-stream' });
  }

  /** Convenience beyond the File API: a base64 data: URL. */
  dataUrl(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(this._source);
    });
  }

  // ---- Deprecated aliases (back-compat) -------------------------------------
  // Renamed to `name` / `type` to match the native File API. Kept so pinned
  // consumers (Outlines2, tutorial, shadow-board) don't break when they upgrade
  // the bundle.
  // TODO(file-api-cleanup): remove `filename` / `filetype` once all consumers
  // have migrated to `name` / `type`.

  /** @deprecated Use `name` (matches File.name). */
  get filename(): string { return this.name; }
  /** @deprecated Use `type` (matches File.type). */
  get filetype(): string { return this.type; }
}
